import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useUnreadSupportTicketCount } from '../../services/notificationService';
import { 
  LayoutDashboard, 
  Users, 
  User,
  UserCheck, 
  FileText, 
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Calendar,
  CreditCard,
  MessageSquare,
  Image,
  Star,
  Database,
  Globe,
  Mail,
  Bell,
  Lock,
  Activity,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Package,
  Receipt,
  Newspaper,
  Briefcase,
  MapPin,
  CheckCircle,
  Eye,
  Download,
  Search,
  RefreshCw,
  HelpCircle,
  Zap,
  Target,
  Award,
  Trophy,
  Banknote,
  BookOpen,
  FileCheck,
  Megaphone,
  LogOut
} from 'lucide-react';

function navPathMatches(href, pathname) {
  if (!href || !pathname) return false;
  if (href === '/admin') {
    return pathname === '/admin' || pathname === '/admin/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const AdminSidebar = () => {
  const location = useLocation();
  const { 
    isAdminSidebarCollapsed, 
    toggleAdminSidebar, 
    isAdminMobileSidebarOpen, 
    isAdminMobileSidebarCollapsed,
    toggleAdminMobileSidebar,
    closeAdminMobileSidebar 
  } = useSidebar();
  const { user, logout } = useAuth();

  const { data: unreadSupportTicketsData } = useUnreadSupportTicketCount();
  const unreadSupportTicketsCount = unreadSupportTicketsData ?? 0;
  
  const [openDropdowns, setOpenDropdowns] = useState({});
  
  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const handleLogout = async () => {
    await logout();
  };
  
  const navigationGroups = [
    // Main Dashboard
    {
      type: 'single',
      items: [
        { name: 'Dashboard', href: '/admin', icon: LayoutDashboard }
      ]
    },
    
    // User Management
    {
      type: 'dropdown',
      name: 'User Management',
      icon: Users,
      items: [
        { name: 'Clients', href: '/admin/clients', icon: User },
        { name: 'Vendors', href: '/admin/vendors', icon: UserCheck },
        // { name: 'User Roles', href: '/admin/user-roles', icon: Shield },
        // { name: 'User Permissions', href: '/admin/permissions', icon: Lock }
      ]
    },
    
    // Business Management
    {
      type: 'dropdown',
      name: 'Business',
      icon: Calendar,
      items: [
        { name: 'Bookings', href: '/admin/bookings', icon: Calendar },
        { name: 'Calendar', href: '/admin/bookings/calendar', icon: Calendar },
        { name: 'Payments', href: '/admin/payments', icon: CreditCard },
        { name: 'Transactions', href: '/admin/transactions', icon: DollarSign },
        { name: 'Refunds', href: '/admin/refunds', icon: RefreshCw },
        { name: 'Subscriptions', href: '/admin/subscriptions', icon: Package },
        { name: 'Subscription Payments', href: '/admin/subscription-payments', icon: Receipt },
        { name: 'Vendor order payouts', href: '/admin/vendor-payouts', icon: Banknote }
      ]
    },
    
    // Content & Media
    {
      type: 'dropdown',
      name: 'Content & Media',
      icon: Image,
      items: [
        { name: 'Content', href: '/admin/content', icon: FileText },
        { name: 'Media Library', href: '/admin/media', icon: Image },
        { name: 'Portfolios', href: '/admin/portfolios', icon: Star },
        { name: 'Reviews', href: '/admin/reviews', icon: MessageSquare },
        { name: 'Vendor Spotlight', href: '/admin/vendor-spotlight', icon: Award },
        { name: 'Vendor Success Stories', href: '/admin/vendor-success-stories', icon: Trophy },
        { name: 'Blog Posts', href: '/admin/blog-posts', icon: FileText },
        // { name: 'Press Releases', href: '/admin/press-releases', icon: Newspaper },
        // { name: 'Job Postings', href: '/admin/job-postings', icon: Briefcase }
      ]
    },
    
    // Analytics & Reports
    {
      type: 'dropdown',
      name: 'Analytics',
      icon: BarChart3,
      items: [
        { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
        { name: 'Advanced Analytics', href: '/admin/advanced-analytics', icon: TrendingUp },
        { name: 'Reports', href: '/admin/reports', icon: FileCheck },
        { name: 'Services report', href: '/admin/reports/services', icon: Package },
        { name: 'Revenue', href: '/admin/revenue', icon: TrendingUp },
        { name: 'Performance', href: '/admin/performance', icon: Activity },
        { name: 'Failed operations', href: '/admin/failed-operations', icon: AlertTriangle }
      ]
    },
    
    // Communication
    {
      type: 'dropdown',
      name: 'Communication',
      icon: MessageSquare,
      items: [
        { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
        { name: 'Contact Messages', href: '/admin/contact-messages', icon: Mail },
        { name: 'Notifications', href: '/admin/notifications', icon: Bell },
        { name: 'System Messages', href: '/admin/system-messages', icon: MessageSquare },
        // { name: 'Email Templates', href: '/admin/email-templates', icon: Mail },
        { name: 'Announcements', href: '/admin/announcements', icon: Megaphone }
      ]
    },
    
    // Services & Locations
    {
      type: 'dropdown',
      name: 'Services',
      icon: Package,
      items: [
        { name: 'Vendor Services', href: '/admin/services', icon: Package },
        { name: 'Countries', href: '/admin/countries', icon: Globe },
        // { name: 'Locations', href: '/admin/locations', icon: MapPin },
        // { name: 'Service Areas', href: '/admin/service-areas', icon: Globe },
        // { name: 'Equipment', href: '/admin/equipment', icon: Package },
        // { name: 'Service Types', href: '/admin/service-types', icon: Target },
        { name: 'Service Categories', href: '/admin/service-categories', icon: Target },
        // { name: 'Photo Booth Types', href: '/admin/photo-booth-types', icon: Image },
        // { name: 'Event Types', href: '/admin/event-types', icon: Calendar },
        // { name: 'Package Types', href: '/admin/package-types', icon: Package }
      ]
    },
    
    // Marketing & Promotions
    {
      type: 'dropdown',
      name: 'Marketing',
      icon: Award,
      items: [
        { name: 'Promotions', href: '/admin/promotions', icon: Award },
        { name: 'Coupons', href: '/admin/coupons', icon: Zap },
        { name: 'Campaigns', href: '/admin/campaigns', icon: Target },
        { name: 'SEO', href: '/admin/seo', icon: Search }
      ]
    },
    
    // System Management
    {
      type: 'dropdown',
      name: 'System',
      icon: Settings,
      items: [
        { name: 'Settings', href: '/admin/settings', icon: Settings },
        { name: 'Security', href: '/admin/security', icon: Shield },
        // { name: 'Database', href: '/admin/database', icon: Database },
        // { name: 'Backups', href: '/admin/backups', icon: Download },
        { name: 'System Logs', href: '/admin/system-logs', icon: Activity }
      ]
    },
    
    // Support & Help
    {
      type: 'dropdown',
      name: 'Support',
      icon: HelpCircle,
      items: [
        { name: 'Support Tickets', href: '/admin/support-tickets', icon: HelpCircle },
        { name: 'FAQ Management', href: '/admin/faqs', icon: BookOpen },
        // { name: 'Documentation', href: '/admin/docs', icon: FileText }
      ]
    },
    
    // Compliance & Legal
    {
      type: 'dropdown',
      name: 'Compliance',
      icon: CheckCircle,
      items: [
        // { name: 'Compliance', href: '/admin/compliance', icon: CheckCircle },
        { name: 'Terms of Service', href: '/admin/terms-of-service', icon: FileText },
        { name: 'Privacy Policy', href: '/admin/privacy-policy', icon: Shield },
        // { name: 'Terms & Privacy', href: '/admin/terms-privacy', icon: FileText },
        // { name: 'Data Protection', href: '/admin/data-protection', icon: Shield },
        { name: 'Compliance audit', href: '/admin/compliance-audit', icon: FileCheck },
        // { name: 'Audit Trail', href: '/admin/audit-trail', icon: Eye }
      ]
    }
  ];

  useEffect(() => {
    const pathname = location.pathname;
    setOpenDropdowns((prev) => {
      const next = { ...prev };
      let changed = false;
      navigationGroups.forEach((group) => {
        if (group.type !== 'dropdown') return;
        const hasActiveChild = group.items.some((item) =>
          navPathMatches(item.href, pathname)
        );
        if (hasActiveChild && !next[group.name]) {
          next[group.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Overlay */}
      {isAdminMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeAdminMobileSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div className={`lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:z-40 transition-all duration-300 ${
        isAdminSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      } ${
        isAdminMobileSidebarOpen 
          ? `fixed inset-y-0 left-0 z-50 pt-16 flex flex-col ${
              isAdminMobileSidebarCollapsed ? 'w-16' : 'w-64'
            }` 
          : 'hidden'
      }`}>
      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-colors">
        {/* Profile Section */}
        <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
                </span>
              </div>
            </div>
            {!isAdminSidebarCollapsed && !isAdminMobileSidebarCollapsed && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-normal text-gray-900 dark:text-gray-100 truncate">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs font-light text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || 'admin@example.com'}
                </p>
                <div className="flex items-center mt-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-light bg-red-100 text-red-800">
                    Admin
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => {
              // Use mobile toggle on mobile, desktop toggle on desktop
              if (window.innerWidth < 1024) {
                toggleAdminMobileSidebar();
              } else {
                toggleAdminSidebar();
              }
            }}
            className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            {(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigationGroups.map((group, groupIndex) => {
            if (group.type === 'single') {
              return group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-200'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    }`
                  }
                  title={(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) ? item.name : undefined}
                >
                  <item.icon className={`h-5 w-5 ${(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
                  {!(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) && item.name}
                </NavLink>
              ));
            }

            if (group.type === 'dropdown') {
              const hasActiveChild = group.items.some((item) =>
                navPathMatches(item.href, location.pathname)
              );
              const isDropdownOpen = hasActiveChild || !!openDropdowns[group.name];
              const isCollapsed = isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed;
              
              return (
                <div key={group.name} className="space-y-1">
                  {/* Dropdown Header */}
                  <button
                    type="button"
                    onClick={() => {
                      if (hasActiveChild) return;
                      toggleDropdown(group.name);
                    }}
                    className={`group w-full flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                      isDropdownOpen || hasActiveChild
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    }`}
                    title={isCollapsed ? group.name : undefined}
                    aria-expanded={isDropdownOpen}
                  >
                    <group.icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left">{group.name}</span>
                        <ChevronDown 
                          className={`h-4 w-4 transition-transform ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`} 
                        />
                      </>
                    )}
                  </button>
                  
                  {/* Dropdown Items */}
                  {!isCollapsed && isDropdownOpen && (
                    <div className="ml-6 space-y-1">
                      {group.items.map((item) => {
                        const badgeCount = item.name === 'Support Tickets' ? unreadSupportTicketsCount : 0;
                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            className={({ isActive }) =>
                              `group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                                isActive
                                  ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/40 dark:text-primary-200'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                              }`
                            }
                          >
                            <div className="flex items-center">
                              <item.icon className="h-4 w-4 mr-3" />
                              {item.name}
                            </div>
                            {badgeCount > 0 && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded-full min-w-[1.25rem] text-center">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return null;
          })}
        </nav>

        {/* Logout Section */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-2 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50 rounded-md transition-colors group"
            title={(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) ? 'Logout' : undefined}
          >
            <LogOut className={`h-5 w-5 ${(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
            {!(isAdminSidebarCollapsed || isAdminMobileSidebarCollapsed) && 'Logout'}
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default AdminSidebar;
